import { useState, useEffect } from "react";
import { Activity, TrendingUp, TrendingDown, ShieldCheck } from "lucide-react";
import { fetchSupplyDemandData } from "../api/client";
import type { SupplyDemandResponse, SupplyDemandHotState } from "../api/types";

function fmtShares(val?: string | number): string {
  if (val === undefined || val === null || val === "") return "—";
  const n = Number(val);
  if (isNaN(n)) return String(val);
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toLocaleString()}주`;
}

function fmtBps(val: number): string {
  const sign = val > 0 ? "+" : "";
  return `${sign}${(val * 100).toFixed(1)}%`;
}

export default function SupplyDemandCard() {
  const [data, setData] = useState<SupplyDemandResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSupplyDemandData()
      .then((res) => setData(res))
      .catch((err) => console.error("Failed to load supply/demand data", err))
      .finally(() => setLoading(false));

    const timer = setInterval(() => {
      fetchSupplyDemandData()
        .then((res) => setData(res))
        .catch(() => {});
    }, 30000); // 30s auto-refresh
    return () => clearInterval(timer);
  }, []);

  if (loading && !data) {
    return (
      <div className="card">
        <div className="card-title-row">
          <div className="card-title">
            <Activity size={16} />
            <span>국내 대표 수급 현황 (삼성전자 · SK하이닉스)</span>
          </div>
        </div>
        <p className="muted-note">수급 데이터를 로딩 중입니다...</p>
      </div>
    );
  }

  const hot = data?.hot_state || {};
  const samsung: SupplyDemandHotState | undefined = hot["005930"];
  const hynix: SupplyDemandHotState | undefined = hot["000660"];
  const bias = data?.macro_bias || { kr_equity_bias: 0, semi_bias: 0 };

  const renderStockTile = (stock?: SupplyDemandHotState, fallbackName = "") => {
    if (!stock) {
      return (
        <div className="metric-tile">
          <div className="metric-tile-head">
            <span className="metric-tile-label">{fallbackName}</span>
          </div>
          <div className="metric-tile-value">—</div>
          <div className="metric-tile-sub">수급 데이터 대기 중</div>
        </div>
      );
    }

    const fNet = Number(stock.foreigner_net || 0);
    const iNet = Number(stock.institution_net || 0);
    const isProvisional = stock.is_provisional === "true";

    return (
      <div className="metric-tile" style={{ padding: "var(--space-4)" }}>
        <div className="metric-tile-head" style={{ marginBottom: "var(--space-2)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
            <span style={{ fontWeight: 600, fontSize: "var(--text-base)" }}>
              {stock.name || fallbackName} ({stock.symbol})
            </span>
            <span className={`badge ${isProvisional ? "badge-yellow" : "badge-green"}`}>
              {isProvisional ? "장중 잠정치" : "장마감 확정"}
            </span>
          </div>
          <span className="muted-note" style={{ fontSize: "var(--text-2xs)" }}>
            {stock.as_of_date || "당일"}
          </span>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-3)", marginTop: "var(--space-2)" }}>
          <div style={{ background: "var(--bg-secondary)", padding: "var(--space-2)", borderRadius: "var(--radius-sm)" }}>
            <div className="muted-note" style={{ fontSize: "var(--text-2xs)", display: "flex", alignItems: "center", gap: 4 }}>
              {fNet >= 0 ? <TrendingUp size={12} color="var(--green)" /> : <TrendingDown size={12} color="var(--red)" />}
              외국인 순매수
            </div>
            <div style={{ fontSize: "var(--text-sm)", fontWeight: 700, color: fNet >= 0 ? "var(--green)" : "var(--red)", marginTop: 2 }}>
              {fmtShares(stock.foreigner_net)}
            </div>
          </div>

          <div style={{ background: "var(--bg-secondary)", padding: "var(--space-2)", borderRadius: "var(--radius-sm)" }}>
            <div className="muted-note" style={{ fontSize: "var(--text-2xs)", display: "flex", alignItems: "center", gap: 4 }}>
              {iNet >= 0 ? <TrendingUp size={12} color="var(--green)" /> : <TrendingDown size={12} color="var(--red)" />}
              기관 순매수
            </div>
            <div style={{ fontSize: "var(--text-sm)", fontWeight: 700, color: iNet >= 0 ? "var(--green)" : "var(--red)", marginTop: 2 }}>
              {fmtShares(stock.institution_net)}
            </div>
          </div>
        </div>

        {stock.credit_balance_rate && (
          <div style={{ marginTop: "var(--space-2)", fontSize: "var(--text-2xs)", color: "var(--text-secondary)" }}>
            신용잔고율: {(Number(stock.credit_balance_rate) * 100).toFixed(2)}%
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="card">
      <div className="card-title-row">
        <div className="card-title">
          <Activity size={16} className="text-primary" />
          <span>24/7 국내 대표 수급 현황 (삼성전자 & SK하이닉스)</span>
          <span className="badge badge-blue">실시간 자동 수집</span>
        </div>
        <div className="card-title-meta">
          <span style={{ fontSize: "var(--text-2xs)", color: "var(--text-secondary)" }}>
            AI 매크로 바이어스: 국장 {fmtBps(bias.kr_equity_bias)} | 반도체 {fmtBps(bias.semi_bias)}
          </span>
        </div>
      </div>

      <div className="grid grid-2" style={{ marginTop: "var(--space-3)" }}>
        {renderStockTile(samsung, "삼성전자")}
        {renderStockTile(hynix, "SK하이닉스")}
      </div>

      <div className="notice-card" style={{ marginTop: "var(--space-3)", padding: "var(--space-2) var(--space-3)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", fontSize: "var(--text-xs)" }}>
          <ShieldCheck size={14} color="var(--accent)" />
          <span>
            국내 수급의 저장 관측입니다. 현재 C1 크립토 전략의 진입·축소 조건에는 포함되지 않으며, 개인 투자 판단에서 집계 시각과 단위를 확인해 사용합니다.
          </span>
        </div>
      </div>
    </div>
  );
}
